import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Plus, FolderKanban, LayoutGrid, List, Pencil, Trash2, X, Loader2, CheckSquare, ChevronDown, Pause, Play, Ban, CheckCircle2, UserCheck, Download } from 'lucide-react'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { ProjectCard } from '@/components/projects/ProjectCard'
import { ProjectFormModal } from '@/components/projects/ProjectFormModal'
import { Badge } from '@/components/common/Badge'
import { ErrorState } from '@/components/common/States'
import { cn } from '@/lib/utils'
import { exportToCsv } from '@/lib/exportCsv'
import { toast } from '@/lib/toast'
import type { Project, ProjectStatus, PaginatedResponse } from '@/types'

const PAGE_SIZE = 30

const STATUS_LABEL: Record<ProjectStatus, string> = {
  DRAFT: 'Bozza',
  PLANNING: 'Pianificazione',
  ACTIVE: 'Attivo',
  ON_HOLD: 'In Pausa',
  COMPLETED: 'Completato',
  CANCELLED: 'Annullato',
}

const STATUS_VARIANT: Record<ProjectStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  DRAFT: 'neutral',
  PLANNING: 'info',
  ACTIVE: 'success',
  ON_HOLD: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
}

export function ProjectsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((state) => state.user)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<ProjectStatus | ''>('')
  const [onlyMine, setOnlyMine] = useState(false)
  const currentYear = new Date().getFullYear()
  const [filterYear, setFilterYear] = useState<number | ''>('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [showForm, setShowForm] = useState(false)
  const [editingProject, setEditingProject] = useState<Project | undefined>(undefined)
  const [exporting, setExporting] = useState(false)

  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectionMode, setSelectionMode] = useState(false)
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)
  const [bulkStatusDropdown, setBulkStatusDropdown] = useState(false)

  const canCreate = user?.isSuperAdmin || user?.role?.permissions?.projects?.includes('create') || false
  const canDelete = user?.isSuperAdmin || user?.role?.permissions?.projects?.includes('delete') || false

  const canFilterMine = (user?.role.level ?? 99) <= 3

  const [page, setPage] = useState(1)

  // Reset alla prima pagina quando cambiano i filtri/ricerca.
  useEffect(() => {
    setPage(1)
  }, [search, filterStatus, onlyMine, filterYear])

  const { data, isLoading, isError, refetch } = useQuery<PaginatedResponse<Project>>({
    queryKey: ['projects', { search, status: filterStatus, onlyMine, year: filterYear, page }],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Project>>('/projects', {
        ...(search ? { search } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(onlyMine && user ? { managerId: user.id } : {}),
        ...(filterYear ? { year: filterYear } : {}),
        page,
        limit: PAGE_SIZE,
      }),
    staleTime: 30_000,
    retry: 1,
  })

  // Il filtro è già applicato server-side (search/status/managerId): non rifiltrare client-side
  // per non falsare i risultati su dataset paginati.
  const projects = data?.items ?? data?.data ?? []
  const total = data?.total ?? data?.pagination?.total ?? projects.length
  const totalPages = data?.totalPages ?? data?.pagination?.totalPages ?? 1
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const rangeEnd = (page - 1) * PAGE_SIZE + projects.length

  // --- Bulk delete mutation ---
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => apiClient.delete(`/projects/${id}`))
      )
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      if (failed.length > 0) {
        const firstErr = (failed[0] as PromiseRejectedResult).reason
        const errMsg = firstErr?.response?.data?.error?.message
          ?? firstErr?.response?.data?.message
          ?? firstErr?.message
          ?? 'Errore sconosciuto'
        const successCount = ids.length - failed.length
        if (successCount > 0) {
          throw new Error(`${successCount} eliminati, ${failed.length} falliti: ${errMsg}`)
        }
        throw new Error(`Eliminazione fallita: ${errMsg}`)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedIds(new Set())
      setSelectionMode(false)
      setBulkDeleteConfirm(false)
    },
    onError: (err: Error) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedIds(new Set())
      setSelectionMode(false)
      setBulkDeleteConfirm(false)
      alert(err.message)
    },
  })

  // --- Bulk status mutation ---
  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: ProjectStatus }) => {
      const results = await Promise.allSettled(
        ids.map((id) => apiClient.patch(`/projects/${id}/status`, { status }))
      )
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      if (failed.length > 0) {
        const firstErr = (failed[0] as PromiseRejectedResult).reason
        const errMsg = firstErr?.response?.data?.error?.message
          ?? firstErr?.response?.data?.message
          ?? firstErr?.message
          ?? 'Errore sconosciuto'
        const successCount = ids.length - failed.length
        if (successCount > 0) {
          throw new Error(`${successCount} aggiornati, ${failed.length} falliti: ${errMsg}`)
        }
        throw new Error(`Aggiornamento fallito: ${errMsg}`)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedIds(new Set())
      setSelectionMode(false)
      setBulkStatusDropdown(false)
    },
    onError: (err: Error) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSelectedIds(new Set())
      setSelectionMode(false)
      setBulkStatusDropdown(false)
      alert(err.message)
    },
  })

  // --- Handlers ---
  function openCreate() {
    setEditingProject(undefined)
    setShowForm(true)
  }

  function openEdit(project: Project) {
    setEditingProject(project)
    setShowForm(true)
  }

  function closeForm() {
    setShowForm(false)
    setEditingProject(undefined)
  }

  async function handleExportCsv() {
    setExporting(true)
    try {
      const result = await apiClient.get<PaginatedResponse<Project>>('/projects', {
        ...(search ? { search } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(onlyMine && user ? { managerId: user.id } : {}),
        ...(filterYear ? { year: filterYear } : {}),
        page: 1,
        limit: 5000,
      })
      exportToCsv('progetti.csv', result.items ?? result.data ?? [], [
        { key: 'name', label: 'Nome', value: (p) => p.name },
        { key: 'client', label: 'Cliente', value: (p) => p.client?.name ?? '' },
        { key: 'status', label: 'Stato', value: (p) => STATUS_LABEL[p.status] ?? p.status },
        { key: 'type', label: 'Tipo', value: (p) => p.type },
        { key: 'manager', label: 'Manager', value: (p) => p.manager ? `${p.manager.firstName} ${p.manager.lastName}` : '' },
        { key: 'startDate', label: 'Data inizio', value: (p) => p.startDate ? new Date(p.startDate).toLocaleDateString('it-IT') : '' },
        { key: 'endDate', label: 'Data fine', value: (p) => p.endDate ? new Date(p.endDate).toLocaleDateString('it-IT') : '' },
        { key: 'createdAt', label: 'Data creazione', value: (p) => new Date(p.createdAt).toLocaleDateString('it-IT') },
        { key: 'budget', label: 'Budget', value: (p) => p.budget ?? '' },
        { key: 'spentBudget', label: 'Speso', value: (p) => p.spentBudget ?? '' },
      ])
    } catch {
      toast.error('Esportazione fallita', 'Impossibile scaricare il CSV dei progetti.')
    } finally {
      setExporting(false)
    }
  }

  const toggleSelect = useCallback((projectId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) {
        next.delete(projectId)
      } else {
        next.add(projectId)
      }
      if (next.size === 0) setSelectionMode(false)
      return next
    })
  }, [])

  function selectAll() {
    setSelectedIds(new Set(projects.map((p) => p.id)))
  }

  function exitSelectionMode() {
    setSelectionMode(false)
    setSelectedIds(new Set())
    setBulkDeleteConfirm(false)
    setBulkStatusDropdown(false)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Progetti</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Gestione e monitoraggio progetti · <span className="tabular-nums">{total}</span> totali
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && !selectionMode && projects.length > 0 && (
            <button
              onClick={() => setSelectionMode(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <CheckSquare className="w-4 h-4" />
              Seleziona
            </button>
          )}
          {canCreate && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Nuovo Progetto
            </button>
          )}
        </div>
      </div>

      {/* Search + filters + view toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Cerca progetti..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 placeholder:text-slate-400 transition-colors"
          />
        </div>

        <select
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value ? parseInt(e.target.value) : '')}
          aria-label="Filtra per anno"
          className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-colors"
        >
          <option value="">Tutti gli anni</option>
          {[currentYear, currentYear - 1, currentYear - 2, currentYear - 3].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <div className="flex items-center gap-1.5 flex-wrap">
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <button
              key={value}
              onClick={() =>
                setFilterStatus(
                  filterStatus === (value as ProjectStatus) ? '' : (value as ProjectStatus)
                )
              }
              className={cn(
                'transition-opacity',
                filterStatus && filterStatus !== value && 'opacity-50'
              )}
            >
              <Badge
                variant={filterStatus === value ? STATUS_VARIANT[value as ProjectStatus] : 'neutral'}
                size="sm"
              >
                {label}
              </Badge>
            </button>
          ))}
        </div>

        {/* I Miei Progetti toggle — visible for L1/L2/L3 */}
        {canFilterMine && (
          <button
            onClick={() => setOnlyMine(!onlyMine)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors',
              onlyMine
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            )}
          >
            <UserCheck className="w-3.5 h-3.5" />
            I Miei Progetti
          </button>
        )}

        <button
          onClick={() => void handleExportCsv()}
          disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border bg-white border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Esporta CSV
        </button>

        <div className="flex items-center gap-1 ml-auto border border-slate-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setViewMode('grid')}
            aria-label="Vista griglia"
            className={cn(
              'p-1.5 transition-colors',
              viewMode === 'grid' ? 'bg-slate-100 text-slate-800' : 'bg-white text-slate-400 hover:bg-slate-50'
            )}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            aria-label="Vista lista"
            className={cn(
              'p-1.5 transition-colors',
              viewMode === 'list' ? 'bg-slate-100 text-slate-800' : 'bg-white text-slate-400 hover:bg-slate-50'
            )}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Select all / deselect bar */}
      {selectionMode && projects.length > 0 && (
        <div className="flex items-center gap-3 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg text-sm">
          <button
            onClick={selectedIds.size === projects.length ? () => setSelectedIds(new Set()) : selectAll}
            className="font-medium text-primary-700 hover:text-primary-800 transition-colors"
          >
            {selectedIds.size === projects.length ? 'Deseleziona tutti' : 'Seleziona tutti'}
          </button>
          <span className="text-primary-700 tabular-nums">
            {selectedIds.size} di {projects.length} selezionati
          </span>
          <button
            onClick={exitSelectionMode}
            className="ml-auto text-slate-500 hover:text-slate-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Projects grid/list */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-44 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card">
          <ErrorState
            title="Impossibile caricare i progetti"
            description="Si è verificato un errore durante il caricamento. Controlla la connessione e riprova."
            onRetry={() => void refetch()}
          />
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-64 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="p-4 bg-slate-50 rounded-full mb-4">
            <FolderKanban className="w-8 h-8 text-slate-300" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            {search || filterStatus || filterYear ? 'Nessun progetto trovato' : 'Nessun progetto ancora'}
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            {search || filterStatus || filterYear
              ? 'Prova a modificare i filtri'
              : 'Crea il primo progetto per iniziare'}
          </p>
          {canCreate && !search && !filterStatus && !filterYear && (
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Crea Progetto
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((project) => {
            const isSelected = selectedIds.has(project.id)
            return (
              <div
                key={project.id}
                className={cn(
                  'relative group',
                  selectionMode && isSelected && 'ring-2 ring-primary-500 rounded-2xl'
                )}
              >
                {/* Selection checkbox */}
                {selectionMode && (
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSelect(project.id) }}
                    className={cn(
                      'absolute top-3 left-3 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors',
                      isSelected
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'bg-white border-slate-200 hover:border-primary-400'
                    )}
                  >
                    {isSelected && (
                      <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                )}
                <ProjectCard
                  project={project}
                  onClick={() => selectionMode ? toggleSelect(project.id) : navigate(`/projects/${project.id}`)}
                />
                {canCreate && !selectionMode && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openEdit(project) }}
                    aria-label="Modifica progetto"
                    className={cn(
                      'absolute top-3 right-3 p-1.5 rounded-lg',
                      'bg-white border border-slate-200 shadow-sm',
                      'text-slate-400 hover:text-slate-700 hover:border-slate-300',
                      // Sempre visibile su touch/mobile, solo in hover da md in su.
                      'opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity'
                    )}
                    title="Modifica progetto"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
          <div className="divide-y divide-slate-200">
            {projects.map((project) => {
              const isSelected = selectedIds.has(project.id)
              return (
                <div
                  key={project.id}
                  className={cn(
                    'flex items-center gap-4 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer',
                    isSelected && 'bg-primary-50 hover:bg-primary-50 border-l-2 border-primary-500'
                  )}
                  onClick={() => selectionMode ? toggleSelect(project.id) : navigate(`/projects/${project.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && (selectionMode ? toggleSelect(project.id) : navigate(`/projects/${project.id}`))}
                >
                  {/* Selection checkbox */}
                  {selectionMode && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(project.id) }}
                      className={cn(
                        'w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                        isSelected
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'bg-white border-slate-200 hover:border-primary-400'
                      )}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900">{project.name}</p>
                    {project.client && (
                      <p className="text-xs text-slate-400 mt-0.5">{project.client.name}</p>
                    )}
                  </div>
                  <Badge variant={STATUS_VARIANT[project.status]} size="sm">
                    {STATUS_LABEL[project.status]}
                  </Badge>
                  {project.teamMembers && project.teamMembers.length > 0 && (
                    <span className="text-xs text-slate-400 tabular-nums">{project.teamMembers.length} membri</span>
                  )}
                  {canCreate && !selectionMode && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openEdit(project) }}
                      aria-label="Modifica progetto"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0"
                      title="Modifica progetto"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !isError && projects.length > 0 && totalPages > 1 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500 tabular-nums">
            {rangeStart}–{rangeEnd} di {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Precedente
            </button>
            <span className="text-sm text-slate-500 tabular-nums px-1">
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Successivo
            </button>
          </div>
        </div>
      )}

      {/* ============ BULK ACTIONS BAR ============ */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-center pb-4 px-4 pointer-events-none">
          <div className="flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-2xl shadow-modal pointer-events-auto">
            <span className="text-sm font-medium">
              <span className="tabular-nums">{selectedIds.size}</span> progett{selectedIds.size === 1 ? 'o' : 'i'} selezionat{selectedIds.size === 1 ? 'o' : 'i'}
            </span>

            <div className="w-px h-5 bg-slate-600" />

            {/* Status change dropdown */}
            <div className="relative">
              <button
                onClick={() => { setBulkStatusDropdown(!bulkStatusDropdown); setBulkDeleteConfirm(false) }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
              >
                Sposta in
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {bulkStatusDropdown && (
                <div className="absolute bottom-full mb-2 left-0 bg-white border border-slate-200 rounded-2xl shadow-dropdown py-1 min-w-[180px]">
                  {([
                    { status: 'ON_HOLD' as ProjectStatus, label: 'In Pausa', icon: Pause, color: 'bg-warning-500' },
                    { status: 'ACTIVE' as ProjectStatus, label: 'Attivo', icon: Play, color: 'bg-success-500' },
                    { status: 'COMPLETED' as ProjectStatus, label: 'Completato', icon: CheckCircle2, color: 'bg-success-500' },
                    { status: 'CANCELLED' as ProjectStatus, label: 'Annullato', icon: Ban, color: 'bg-danger-500' },
                  ]).map(({ status, label, icon: Icon, color }) => (
                    <button
                      key={status}
                      onClick={() => {
                        bulkStatusMutation.mutate({ ids: Array.from(selectedIds), status })
                      }}
                      disabled={bulkStatusMutation.isPending}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                    >
                      <div className={cn('w-2 h-2 rounded-full', color)} />
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Delete */}
            {!bulkDeleteConfirm ? (
              <button
                onClick={() => { setBulkDeleteConfirm(true); setBulkStatusDropdown(false) }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-danger-600 hover:bg-danger-700 rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Elimina
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-danger-300">Confermi?</span>
                <button
                  onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                  disabled={bulkDeleteMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-danger-600 hover:bg-danger-700 rounded-lg transition-colors"
                >
                  {bulkDeleteMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                  Si, elimina
                </button>
                <button
                  onClick={() => setBulkDeleteConfirm(false)}
                  className="px-2 py-1.5 text-xs font-medium text-slate-300 hover:text-white transition-colors"
                >
                  No
                </button>
              </div>
            )}

            <div className="w-px h-5 bg-slate-600" />

            {/* Cancel selection */}
            <button
              onClick={exitSelectionMode}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <ProjectFormModal
          project={editingProject}
          onClose={closeForm}
          onSuccess={closeForm}
        />
      )}
    </div>
  )
}
